import React, { useEffect, useRef, useState } from "react";
import { Button, Col, DatePicker, Form, Input, Modal, notification, Row, Select, Table, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { getPaymentTransactionsService } from "../../../services/admin-services";
import { formatIndianCurrency } from "../../../utills/helpers";

const { TextArea } = Input;

const mapPaymentListToRows = (list) => {
  if (!Array.isArray(list)) return [];
  return list.map((item) => ({
    key: String(item.id),
    id: item.id,
    transactionId: String(item.id),
    paymentFor: item.payment_type,
    amount: item.amount,
    payment_date: item.payment_date,
    paidOn: item.payment_date
      ? dayjs(item.payment_date).format("DD/MM/YYYY")
      : "-",
    mode: item.payment_mode,
    mr_utr_number: item.mr_utr_number,
    remarks: item.remarks,
    file_name: item.file_name,
    file_path: item.file_path,
    created_by: item.created_by,
  }));
};

const getReversalRemarksPrefix = (transactionId) =>
  transactionId ? `Reversal of Transaction ID ${transactionId}` : "";

const enforceReversalRemarksPrefix = (value, transactionId) => {
  const prefix = getReversalRemarksPrefix(transactionId);
  if (!prefix) return value;

  const raw = typeof value === "string" ? value : "";
  if (!raw.trim()) return `${prefix}\n`;
  if (raw.startsWith(prefix)) return raw;

  // If user edits/deletes the prefix, restore it and keep only their appended text.
  const stripped = raw
    .replace(/^Reversal of Transaction ID\s+\S+\s*\n?/i, "")
    .replace(/^\n+/, "");
  return `${prefix}\n${stripped}`;
};

/**
 * Reusable Transaction Modal used in Payments and other places (e.g. Bookings)
 *
 * This component is intentionally "dumb" – all business logic (API calls, state)
 * is owned by the parent and passed in via props, so existing flows remain unchanged.
 */
const TransactionModal = ({
  open,
  onCancel,
  form,
  onFinish,
  isEditTransaction,
  isLeadOpen,
  // isRefundEnabled,
  saveLoading = false, // when true, Save shows loader and buttons are disabled to prevent duplicate submit
  transactionFile,
  onTransactionFileChange,
  validateTransactionFile,
  fixedPaymentType, // optional: e.g. "Booking" when opened from Book button
  onlyBookingAllowed = false, // when true (and creating new txn), disable all Payment For options except Booking
  leadId, // required to load reversal candidates (normal entries without reversal)
}) => {
  const [isOriginalTransactionModalOpen, setIsOriginalTransactionModalOpen] = useState(false);
  const [selectedOriginalTransactionKey, setSelectedOriginalTransactionKey] = useState(null);
  const [selectedOriginalTransaction, setSelectedOriginalTransaction] = useState(null);
  const [reversalEligibleRows, setReversalEligibleRows] = useState([]);
  /** In-flight list fetch (no spinner — only gates the button to avoid stale picks). */
  const [listFetchPending, setListFetchPending] = useState(false);
  const prevWasReversalRef = useRef(false);

  // Ensure payment_for is set when fixedPaymentType is provided and modal is opened
  useEffect(() => {
    if (open && fixedPaymentType) {
      form.setFieldsValue({ payment_for: fixedPaymentType });
    }
  }, [open, fixedPaymentType, form]);

  // New transaction: default Transaction Mode to Normal Entry when the modal opens
  useEffect(() => {
    if (!open || isEditTransaction) return;
    form.setFieldsValue({ transaction_mode: "normal" });
  }, [open, isEditTransaction, form]);

  // Watch payment_mode to conditionally require MR/UTR Number
  const paymentMode = Form.useWatch("payment_mode", form);
  // Watch transaction_mode for reversal UI and required remarks
  const transactionType = Form.useWatch("transaction_mode", form);
  const paymentFor = Form.useWatch("payment_for", form);
  const isReversalEntry = transactionType === "reversal";

  useEffect(() => {
    if (!open) {
      prevWasReversalRef.current = false;
    }
  }, [open]);

  // Normal Entry: clear remarks & attachment. Prefill from original txn only applies after Reversal + table confirm.
  useEffect(() => {
    if (!open) return;
    if (isReversalEntry) {
      prevWasReversalRef.current = true;
      return;
    }
    setIsOriginalTransactionModalOpen(false);
    setSelectedOriginalTransaction(null);
    setSelectedOriginalTransactionKey(null);
    const hadOriginal = form.getFieldValue("reverses_transaction_id");
    if (hadOriginal) {
      form.setFieldsValue({
        reverses_transaction_id: undefined,
        amount: undefined,
        payment_for: fixedPaymentType || undefined,
        payment_mode: undefined,
        mr_utr_number: undefined,
        date_of_payment: dayjs(),
        remarks: undefined,
      });
      if (!isEditTransaction) {
        onTransactionFileChange?.({ fileList: [] });
      }
    } else if (prevWasReversalRef.current && !isEditTransaction) {
      form.setFieldsValue({ remarks: undefined });
      onTransactionFileChange?.({ fileList: [] });
    }
    prevWasReversalRef.current = false;
    // onTransactionFileChange omitted from deps: parent may pass an inline fn (e.g. Bookings) and would re-run this effect every render.
  }, [isReversalEntry, form, fixedPaymentType, open, isEditTransaction]);

  // Clear validation errors when switching to Cash
  useEffect(() => {
    if (open && paymentMode === "Cash") {
      form.setFields([{ name: "mr_utr_number", errors: [] }]);
    }
  }, [paymentMode, open, form]);

  // Reversal: amount / payment mode / MR-UTR / date are disabled until original txn is selected — drop stale errors from Normal Entry submit
  useEffect(() => {
    if (!open || !isReversalEntry) return;
    form.setFields([
      { name: "amount", errors: [] },
      { name: "payment_mode", errors: [] },
      { name: "mr_utr_number", errors: [] },
      { name: "date_of_payment", errors: [] },
    ]);
  }, [open, isReversalEntry, form]);
  const restrictToBooking = onlyBookingAllowed && !isEditTransaction && !fixedPaymentType;

  // If Payment For changes in Reversal mode, clear previously selected original txn defaults.
  useEffect(() => {
    if (!open || !isReversalEntry) return;
    const hadOriginal = form.getFieldValue("reverses_transaction_id");
    if (!hadOriginal) return;
    setSelectedOriginalTransaction(null);
    setSelectedOriginalTransactionKey(null);
    setIsOriginalTransactionModalOpen(false);
    form.setFieldsValue({
      reverses_transaction_id: undefined,
      amount: undefined,
      payment_mode: undefined,
      mr_utr_number: undefined,
      date_of_payment: dayjs(),
      remarks: undefined,
    });
    onTransactionFileChange?.({ fileList: [] });
    form.setFields([
      { name: "amount", errors: [] },
      { name: "payment_mode", errors: [] },
      { name: "mr_utr_number", errors: [] },
      { name: "date_of_payment", errors: [] },
      { name: "remarks", errors: [] },
    ]);
    // onTransactionFileChange omitted: parent may pass inline function in some flows.
  }, [paymentFor, open, isReversalEntry, form]);

  // Original-transaction list API: when Payment For + Reversal Entry are both selected (transaction mode drives reversal).
  useEffect(() => {
    if (!open || !leadId || !isReversalEntry || !paymentFor) {
      setReversalEligibleRows([]);
      setListFetchPending(false);
      setSelectedOriginalTransactionKey(null);
      setIsOriginalTransactionModalOpen(false);
      return;
    }
    let cancelled = false;
    setSelectedOriginalTransactionKey(null);
    setIsOriginalTransactionModalOpen(false);
    setListFetchPending(true);
    (async () => {
      try {
        const res = await getPaymentTransactionsService({
          lead_id: leadId,
          page: 1,
          per_page: 100,
          search: undefined,
          payment_type: paymentFor,
          payment_mode: "",
          normal_without_reversal: true,
        });
        const list = res?.data?.data?.list_data || [];
        if (cancelled) return;
        setReversalEligibleRows(mapPaymentListToRows(list));
      } catch (error) {
        if (!cancelled) {
          notification.error({
            description: error?.response?.data?.message || error?.message,
          });
          setReversalEligibleRows([]);
        }
      } finally {
        if (!cancelled) setListFetchPending(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, leadId, isReversalEntry, paymentFor]);

  const handleSelectTransactionClick = () => {
    if (
      !leadId ||
      !paymentFor ||
      !isReversalEntry ||
      listFetchPending ||
      reversalEligibleRows.length === 0
    ) {
      return;
    }
    setIsOriginalTransactionModalOpen(true);
  };

  const selectTransactionDisabled =
    !leadId ||
    !paymentFor ||
    listFetchPending ||
    reversalEligibleRows.length === 0;

  const originalTransactionColumns = [
    {
      title: "Payment For",
      dataIndex: "paymentFor",
      key: "paymentFor",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (value) =>
        value != null && value !== ""
          ? formatIndianCurrency(Number(value))
          : "-",
    },
    {
      title: "Paid on",
      dataIndex: "paidOn",
      key: "paidOn",
    },
    {
      title: "Payment Mode",
      dataIndex: "mode",
      key: "mode",
    },
    {
      title: "Updated By",
      dataIndex: "created_by",
      key: "created_by",
      render: (text) => text || "-",
    },
  ];

  const handleConfirmOriginalTransaction = () => {
    if (!selectedOriginalTransactionKey) {
      return;
    }
    const selectedRow = reversalEligibleRows.find(
      (item) => item.key === selectedOriginalTransactionKey
    );
    setSelectedOriginalTransaction(selectedRow || null);
    setIsOriginalTransactionModalOpen(false);
    if (selectedRow) {
      const reversalPrefix = getReversalRemarksPrefix(selectedRow.id);
      form.setFieldsValue({
        payment_for: selectedRow.paymentFor,
        amount: selectedRow.amount,
        payment_mode: selectedRow.mode,
        mr_utr_number: selectedRow.mr_utr_number ?? "",
        date_of_payment: selectedRow.payment_date
          ? dayjs(selectedRow.payment_date)
          : dayjs(),
        transaction_mode: "reversal",
        reverses_transaction_id: selectedRow.id,
        remarks: `${reversalPrefix}\n`,
      });
      if (selectedRow.file_path) {
        onTransactionFileChange?.({
          fileList: [
            {
              uid: `prefill-original-${selectedRow.id}`,
              name: selectedRow.file_name || "attachment",
              status: "done",
              url: selectedRow.file_path,
            },
          ],
        });
      } else {
        onTransactionFileChange?.({ fileList: [] });
      }
      form.setFields([
        { name: "amount", errors: [] },
        { name: "payment_mode", errors: [] },
        { name: "mr_utr_number", errors: [] },
        { name: "date_of_payment", errors: [] },
      ]);
    }
  };

  const handleSaveClick = () => {
    if (isReversalEntry && !selectedOriginalTransaction) {
      notification.error({
        message: "Please select the original transaction",
      });
      return;
    }
    form.submit();
  };

  return (
    <>
      <Modal
        title={
          <div>
            <h5 className=" mb-0" style={{ fontSize: "20px", fontWeight: "500" }}>
              {isEditTransaction ? "Update Transaction" : "Transaction"}
            </h5>
            <hr style={{ margin: "15px 0 0 0" }} />
          </div>
        }
        open={open}
        onCancel={onCancel}
        closable={!saveLoading}
        maskClosable={false}
        width={800}
        footer={[
          <Button
            key="cancel"
            onClick={onCancel}
            ghost
            danger
            className="primary-button"
            disabled={saveLoading}
          >
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            className="secondary-button"
            loading={saveLoading}
            disabled={saveLoading}
            onClick={handleSaveClick}
          >
            Save
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            date_of_payment: dayjs(),
            transaction_mode: "normal",
            ...(fixedPaymentType ? { payment_for: fixedPaymentType } : {}),
          }}
        >
          <Form.Item name="reverses_transaction_id" hidden>
            <Input />
          </Form.Item>
          <Row gutter={[16, 16]}>


          <Col xl={12} lg={12} md={12} sm={24} xs={24}>
            <Form.Item
              name="payment_for"
              label="Payment For"
              rules={[{ required: true, message: "Please select payment for" }]}
            >
              <Select
                placeholder="Select One"
                className="form-control-ant"
                disabled={isEditTransaction || !!fixedPaymentType}
              >
                <Select.Option value="Booking" disabled={!isLeadOpen}>
                  Booking
                </Select.Option>
                <Select.Option
                  value="Down Payment"
                  disabled={!isLeadOpen || restrictToBooking}
                >
                  Down Payment
                </Select.Option>
                <Select.Option
                  value="Delivery Order"
                  disabled={!isLeadOpen || restrictToBooking}
                >
                  Delivery Order
                </Select.Option>
                <Select.Option
                  value="Disbursement"
                  disabled={!isLeadOpen || restrictToBooking}
                >
                  Disbursement
                </Select.Option>
                {/* Refund enabled in all lead statuses; disabled only when opened from Booking flow */}
                <Select.Option value="Refund" disabled={restrictToBooking}>
                  Refund
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xl={12} lg={12} md={12} sm={24} xs={24}>
            <Form.Item
              label="Transaction Mode"
              required
            >
              <div style={{ display: "flex", gap: 0, alignItems: "center" }}>
                <Form.Item
                  name="transaction_mode"
                  rules={[{ required: true, message: "Please select transaction mode" }]}
                  style={{ marginBottom: 0, flex: "1 1 auto" }}
                >
                  <Select
                    placeholder="Select Transaction Mode"
                    className="form-control-ant"
                  >
                    <Select.Option value="normal">
                      Normal Entry
                    </Select.Option>
                    <Select.Option
                      value="reversal"
                      disabled={
                        (!isEditTransaction && fixedPaymentType === "Booking") ||
                        (onlyBookingAllowed && !isEditTransaction)
                      }
                    >
                      Reversal Entry
                    </Select.Option>
                  </Select>
                </Form.Item>
                {isReversalEntry && (
                  <Button
                    type="primary"
                    disabled={selectTransactionDisabled}
                    style={{
                      backgroundColor: "#198754",
                      borderColor: "#198754",
                      height: 37,
                      minWidth: 148,
                      flexShrink: 0,
                      padding: "0 10px",
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      borderTopRightRadius: 6,
                      borderBottomRightRadius: 6,
                      whiteSpace: "nowrap",
                      marginLeft: -1,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: 1,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                    onClick={handleSelectTransactionClick}
                  >
                    Select Transaction
                  </Button>
                )}
              </div>
             
            </Form.Item>
          </Col>
          <Col xl={12} lg={12} md={12} sm={24} xs={24}>
            <Form.Item
              name="amount"
              label="Amount"
              validateTrigger={["onChange", "onBlur"]}
              rules={[
                { required: true, message: "Please input amount" },
                () => ({
                  validator(_, value) {
                    if (value === null || value === undefined || value === "") {
                      return Promise.resolve();
                    }
                    const num = Number(value);
                    if (Number.isNaN(num)) return Promise.reject("Invalid amount");
                    if (num === 0) return Promise.reject("Amount cannot be zero");
                    if (num < 0) return Promise.reject("Amount must be positive");
                    if (num > 99999999999) return Promise.reject("Amount is too large");
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input
                placeholder="Enter Amount"
                className="form-control-ant"
                type="number"
                step="1"
                disabled={isReversalEntry}
                onKeyDown={(e) => {
                  if (isReversalEntry) {
                    e.preventDefault();
                    return;
                  }
                  if (["e", "E", "+", "-"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>
          </Col>

          <Col xl={12} lg={12} md={12} sm={24} xs={24}>
            <Form.Item
              name="payment_mode"
              label="Payment Mode"
              rules={[{ required: true, message: "Please select payment mode" }]}
            >
              <Select
                placeholder="Select One"
                className="form-control-ant"
                disabled={isReversalEntry}
              >
                <Select.Option value="Cash">Cash</Select.Option>
                <Select.Option value="UPI">UPI</Select.Option>
                <Select.Option value="IMPS">IMPS</Select.Option>
                <Select.Option value="NEFT">NEFT</Select.Option>
                <Select.Option value="RTGS">RTGS</Select.Option>
                <Select.Option value="Cheque">Cheque</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xl={12} lg={12} md={12} sm={24} xs={24}>
            <Form.Item
              name="mr_utr_number"
              label={
                <span>
                  MR / UTR Number{" "}
                  {paymentMode !== "Cash" && (
                    <span style={{ color: "red" }}>*</span>
                  )}
                </span>
              }
              validateTrigger={["onSubmit"]}
              rules={[
                {
                  validator: (_, value) => {
                    const currentPaymentMode = form.getFieldValue("payment_mode");
                    // If payment mode is Cash, MR/UTR is optional - no validation needed
                    if (currentPaymentMode === "Cash") {
                      // If value is provided, validate format only
                      if (value) {
                        if (!/^[A-Za-z0-9]+$/.test(value)) {
                          return Promise.reject("MR/UTR number must be alphanumeric");
                        }
                        if (value.length > 20) {
                          return Promise.reject(
                            "MR/UTR number cannot exceed 20 characters"
                          );
                        }
                      }
                      return Promise.resolve();
                    }
                    // For non-Cash payment modes, MR/UTR is required
                    if (!value) {
                      return Promise.reject("Please input MR/UTR number");
                    }
                    if (!/^[A-Za-z0-9]+$/.test(value)) {
                      return Promise.reject("MR/UTR number must be alphanumeric");
                    }
                    if (value.length > 20) {
                      return Promise.reject(
                        "MR/UTR number cannot exceed 20 characters"
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                placeholder="Enter Number"
                className="form-control-ant"
                maxLength={20}
                disabled={isReversalEntry}
              />
            </Form.Item>
          </Col>

          <Col xl={12} lg={12} md={12} sm={24} xs={24}>
            <Form.Item
              name="date_of_payment"
              label="Date of Payment"
              rules={[{ required: true, message: "Please select date" }]}
            >
              <DatePicker
                className="form-control-ant"
                style={{ width: "100%" }}
                format="DD MMM YYYY"
                placeholder="01 Oct 2025"
                disabled={isReversalEntry}
                disabledDate={(current) => {
                  return current && current > dayjs().endOf('day');
                }}
              />
            </Form.Item>
          </Col>

          <Col xl={12} lg={12} md={12} sm={24} xs={24}>
            <Form.Item label="Attachment">
              <Upload
                beforeUpload={validateTransactionFile}
                maxCount={1}
                accept=".jpg,.jpeg,.png,.gif,.pdf"
                customRequest={({ file, onSuccess }) => {
                  // We handle upload via form submit, so mark as success immediately
                  // eslint-disable-next-line no-console
                  console.log(file);
                  onSuccess("ok");
                }}
                onChange={onTransactionFileChange}
                fileList={transactionFile}
                showUploadList={{ showRemoveIcon: true }}
              >
                <Button
                  icon={<UploadOutlined />}
                  style={{
                    borderColor: "#d9d9d9",
                    color: "#595959",
                    backgroundColor: "#fff",
                  }}
                />
              </Upload>
            </Form.Item>
          </Col>

          <Col xl={24} lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name="remarks"
              label={
                <span>
                  Remarks{" "}
                  {transactionType === "reversal" && (
                    <span style={{ color: "red" }}>*</span>
                  )}
                </span>
              }
              dependencies={["transaction_mode"]}
              normalize={(value) => {
                if (!isReversalEntry) return value;
                const reversalId =
                  selectedOriginalTransaction?.id ??
                  form.getFieldValue("reverses_transaction_id");
                return enforceReversalRemarksPrefix(value, reversalId);
              }}
              rules={[
                {
                  validator: (_, value) => {
                    const mode = form.getFieldValue("transaction_mode");
                    if (mode === "reversal" && (!value || !String(value).trim())) {
                      return Promise.reject("Please enter remarks");
                    }
                    return Promise.resolve();
                  },
                },
                {
                  max: 200,
                  message: "Remarks cannot exceed 200 characters",
                },
              ]}
            >
              <TextArea
                rows={4}
                placeholder="Enter Remarks"
                maxLength={200}
                showCount
              />
            </Form.Item>
          </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Select Original Transaction"
        open={isOriginalTransactionModalOpen}
        onCancel={() => {
          setIsOriginalTransactionModalOpen(false);
          setSelectedOriginalTransactionKey(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setIsOriginalTransactionModalOpen(false);
              setSelectedOriginalTransactionKey(null);
            }}
          >
            Close
          </Button>,
          <Button
            key="confirm"
            type="primary"
            className="secondary-button"
            disabled={!selectedOriginalTransactionKey}
            onClick={handleConfirmOriginalTransaction}
          >
            Confirm Selection
          </Button>,
        ]}
        width={760}
      >
        <div
          style={{
            maxHeight: 280,
            overflow: "auto",
            width: "100%",
          }}
        >
          <Table
            columns={originalTransactionColumns}
            dataSource={reversalEligibleRows}
            locale={{ emptyText: "No eligible transactions" }}
            rowKey="key"
            rowSelection={{
              type: "radio",
              selectedRowKeys: selectedOriginalTransactionKey
                ? [selectedOriginalTransactionKey]
                : [],
              onChange: (selectedRowKeys) => {
                setSelectedOriginalTransactionKey(selectedRowKeys?.[0] || null);
              },
            }}
            pagination={false}
            size="small"
          />
        </div>
      </Modal>
    </>
  );
};

export default TransactionModal;


